'use client';

import { useEffect, useRef, useState, Suspense } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';

import { getDict, NavBarDict, BlogDict, FooterDict } from '@/lib/dictionaries';
import { Locale } from '@/lib/i18n';

// ✅ Server Components
 import Footer from '@/app/components/ui/Footer';
import Breadcrumb from '@/app/components/ui/Breadcrumb';

const NavBar = dynamic(() => import('@/app/components/navBar/NavBar'), { ssr: false });

// ✅ Dynamic imports + Lazy Hydration
const SearchBox = dynamic(() => import('@/app/components/blog/SearchBox'), {
  ssr: false,
  loading: () => (
    <div className="w-full h-24 bg-gray-100 animate-pulse rounded-lg" />
  ),
});
const CategoriesBox = dynamic(() => import('@/app/components/blog/CategoriesBox'), { ssr: false });
const KeywordsBox = dynamic(() => import('@/app/components/blog/KeywordsBox'), { ssr: false });

import BlogCardSkeleton from '@/app/components/blog/BlogPostSkeleton';
import Pagination from '@/app/components/blog/Pagination';
import LazyHydrateOnScroll from '@/app/components/LazyHydrateOnScroll';
import Link from 'next/link';

interface Category {
  id: number;
  name: string;
}
interface Tag {
  id: number;
  name: string;
}

interface BlogPageUIProps {
  locale: Locale;
  initialPosts: any[];
  totalPages: number;
  allCategories: Category[];
  allTags: Tag[];
  dict: BlogDict;
  footerDict: FooterDict;
  hasError?: boolean; // ✅ nouvel indicateur
}

export default function BlogPageUI({
  locale,
  initialPosts,
  totalPages,
  allCategories,
  allTags,
  dict,
  footerDict,
  hasError = false,
}: BlogPageUIProps) {
  // ✅ States
const [posts, setPosts] = useState(initialPosts);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedKeyword, setSelectedKeyword] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pagesCache, setPagesCache] = useState<Record<number, any[]>>({});
  const [navbarDict, setNavbarDict] = useState<NavBarDict | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const prevCategoryRef = useRef<number | null>(null);
  const prevKeywordRef = useRef<number | null>(null);
 const blogsPerPage = 3;

 useEffect(() => {
  let isMounted = true;

  const fetchPosts = async () => {
     if (pagesCache[currentPage]) {
      setPosts(pagesCache[currentPage]);
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`/api/blog?locale=${locale}&page=${currentPage}`);
      const data = await res.json();

      if (isMounted) {
        setPosts(data.posts);

         setPagesCache(prev => ({
          ...prev,
          [currentPage]: data.posts,
        }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (isMounted) setLoading(false);
    }
  };

  fetchPosts();

  return () => {
    isMounted = false;
  };
}, [currentPage]);


  // ✅ Load navbar dict
  useEffect(() => {
    getDict(locale, 'navbar').then(setNavbarDict);
  }, [locale]);

  
  // ✅ Reset page and restore list position when filters/search change
  useEffect(() => {
    setCurrentPage(1);
    if (listRef.current) {
      listRef.current.scrollIntoView({ behavior: 'auto', block: 'nearest' });
    }
  }, [searchTerm, selectedCategory, selectedKeyword]);

  // ✅ Fermer le sidebar après sélection de catégorie, mot-clé, ou réinitialisation
  useEffect(() => {
    // Fermer le sidebar si on a sélectionné quelque chose
    if (selectedCategory !== null || selectedKeyword !== null) {
      setSidebarOpen(false);
    }
    // Fermer aussi si on réinitialise (clique sur "All") et qu'on avait quelque chose avant
    else if (prevCategoryRef.current !== null || prevKeywordRef.current !== null) {
      setSidebarOpen(false);
    }
    
    // Mettre à jour les refs pour la prochaine fois
    prevCategoryRef.current = selectedCategory;
    prevKeywordRef.current = selectedKeyword;
  }, [selectedCategory, selectedKeyword]);

  // ✅ Smooth scroll to top on page change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);


  // ✅ Filtrage
  const filteredPosts = posts.filter((post) => {
    const matchTitle =
      !searchTerm || post.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategory =
      !selectedCategory || post.categories?.includes(selectedCategory);
    const matchKeyword =
      !selectedKeyword || post.tags?.includes(selectedKeyword);
    return matchTitle && matchCategory && matchKeyword;
  });

  const paginatedPosts = filteredPosts;

  if (!navbarDict) return null;

  return (
    <>
      {/* ✅ Header */}
      <section className="min-h-[35vh] bg-[#330052] text-white font-sans">
        <NavBar activeUnderline={dict.activeUnderlineTitle} />
        <div className="flex flex-col items-center justify-center text-center py-10 md:py-3 px-4">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">{dict.title}</h1>
          <Breadcrumb
            homeLabel={dict.breadCrumbHome}
            currentLabel={dict.title}
            locale={locale}
          />
        </div>
      </section>

      <main className="min-h-screen bg-gray-50 py-6 md:py-12 px-4">
        {/* Bouton moderne pour ouvrir le sidebar sur tablette/mobile - affiché seulement s'il y a des articles */}
        {filteredPosts.length > 0 && (
          <div className="xl:hidden fixed bottom-6 right-6 z-50">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="bg-[#330052] hover:bg-[#4B2FA6] text-white p-4 rounded-full shadow-lg transition-all duration-300 ease-out hover:shadow-xl hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#ffd900] focus:ring-offset-2"
              aria-label={locale === 'fr' ? 'Ouvrir les filtres' : 'Open filters'}
            >
              <svg 
                className={`w-6 h-6 transition-transform duration-300 ${sidebarOpen ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </button>
          </div>
        )}

        {/* Overlay transparent pour fermer le sidebar */}
        {sidebarOpen && (
          <div 
            className="xl:hidden fixed inset-0 z-40 transition-opacity duration-300"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <div className="max-w-7xl mx-auto grid grid-cols-1 xl:grid-cols-3 gap-6 md:gap-8">
        {
         (hasError || (paginatedPosts.length === 0 && !loading && posts.length > 0))
          ? (
          <div className="xl:col-span-3 flex flex-col items-center justify-center py-16">
            <Image
              src={hasError ? "/error.svg" : "/empty.svg"}
              alt={hasError ? "Erreur lors du chargement" : "Aucun article"}
              width={272}
              height={272}
              className="mb-6"
              decoding="async"
              loading="lazy"
            />
            <p className="text-gray-600 text-lg font-medium text-center">
              {hasError 
                ? 'Une erreur est survenue. Veuillez réessayer plus tard.' 
                : dict.noArticles || 'Aucun article disponible pour le moment.'}
            </p>
          </div>
        ) : (
          <>
            {/* ✅ Liste des posts */}
           <div className="xl:col-span-2" ref={listRef}>
  {loading  ? (
    <div className="space-y-8 md:space-y-12">
      {Array.from({ length: blogsPerPage }).map((_, idx) => (
        <BlogCardSkeleton key={idx} />
      ))}
    </div>
  ) : (
    <div className="space-y-8 md:space-y-12">
      {paginatedPosts.map((post, idx) => (
        <article key={post.id} className="mb-8 md:mb-12">
          <div className="relative w-full max-w-3xl mx-auto xl:ml-24">
            {post.imageSecondaire ? (
              <Image
                src={post.imageSecondaire}
                alt={post.title}
                width={1200}
                height={600}
                className="w-full h-48 md:h-72 object-cover rounded-2xl"
                priority={idx === 0}
                decoding="async"
                loading={idx === 0 ? 'eager' : 'lazy'}
              />
            ) : (
              <div className="w-full h-48 md:h-72 bg-gray-300 rounded-2xl flex items-center justify-center">
                <span className="text-gray-400 text-sm md:text-base">
                  Aucune image
                </span>
              </div>

            )}
            {post?.date && (
              <div className="absolute top-3 md:top-4 left-3 md:left-4 w-12 md:w-14 rounded-lg overflow-hidden shadow">
                <div className="bg-[#ffd900] text-white text-center font-bold text-base md:text-lg py-0.5 md:py-1">
                  {new Date(post.date).getDate()}
                </div>
                <div className="bg-gray-100 text-[#330052] text-center font-bold uppercase text-[10px] md:text-sm py-0.5 md:py-1 tracking-wider">
                  {new Date(post.date).toLocaleString('fr-FR', { month: 'short' })}
                </div>
              </div>
            )}
            <div className="pt-3 md:pt-4">
              <div className="flex items-center text-[10px] md:text-xs text-gray-500 mb-1.5 md:mb-2 space-x-1">
                <svg className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#ffd900] mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 10a4 4 0 100-8 4 4 0 000 8zm0 2c-4 0-7 2-7 4v1a1 1 0 001 1h12a1 1 0 001-1v-1c0-2-3-4-7-4z"/>
                </svg>
                <span className="flex items-center h-4">{dict.byAdmin}</span>
              </div>
              <h2 className="text-lg md:text-2xl font-bold mb-2 text-[#330052]">
                {post.title}
              </h2>
              <div dangerouslySetInnerHTML={{ __html: post.excerpt }} />
               <h2>
                {post.sousTitre}
              </h2>
              <Link
                href={`/${locale}/blog/${post.slug}`}
                className="inline-block bg-[#330052] text-white px-4 py-2 rounded-lg mt-2 font-bold transition-all duration-200 ease-out hover:bg-[#4B2FA6] hover:shadow-lg hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd900] focus-visible:ring-offset-2"
              >
                {dict.readMore}
              </Link>
            </div>
          </div>
        </article>
      ))}
    </div>
  )}

                {/* Afficher la pagination seulement s'il y a plus de 3 blogs */}
                {filteredPosts.length > 3 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    dict={dict}
                  />
                )}
              </div>

              {/* ✅ Sidebar responsive - affiché seulement s'il y a des articles */}
              {filteredPosts.length > 0 && (
                <aside className={`
                  xl:flex xl:flex-col xl:items-stretch xl:mt-0
                  fixed xl:relative top-0 right-0 h-full xl:h-auto w-80 xl:w-auto
                  bg-white xl:bg-transparent shadow-2xl xl:shadow-none
                  transform transition-transform duration-300 ease-in-out z-50
                  ${sidebarOpen ? 'translate-x-0' : 'translate-x-full xl:translate-x-0'}
                  flex flex-col items-stretch mt-0
                `}>
                {/* Header du sidebar mobile avec bouton fermer */}
                <div className="xl:hidden flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
                  <h3 className="text-lg font-semibold text-[#330052]">
                    {locale === 'fr' ? 'Filtres' : 'Filters'}
                  </h3>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
                    aria-label={locale === 'fr' ? 'Fermer les filtres' : 'Close filters'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Contenu du sidebar avec scroll */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 xl:p-0 space-y-6 min-h-0">
                  <Suspense fallback={<div>Chargement filtres...</div>}>
                    <LazyHydrateOnScroll placeholderHeight="300px">
                      <SearchBox
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        dict={dict}
                      />
                    </LazyHydrateOnScroll>

                    <LazyHydrateOnScroll placeholderHeight="200px">
                      <div className="w-full">
                        <CategoriesBox
                          posts={posts}
                          allCategories={allCategories}
                          selectedCategory={selectedCategory}
                          setSelectedCategory={setSelectedCategory}
                          dict={dict}
                        />
                      </div>
                    </LazyHydrateOnScroll>

                    <LazyHydrateOnScroll placeholderHeight="200px">
                      <KeywordsBox
                        allTags={allTags}
                        selectedKeyword={selectedKeyword}
                        setSelectedKeyword={setSelectedKeyword}
                        posts={filteredPosts}
                        dict={dict}
                      />
                    </LazyHydrateOnScroll>
                   </Suspense>
                 </div>
               </aside>
              )}
            </>
          )}
        </div>
      </main>

      <Footer dict={footerDict} locale={locale} />
    </>
  );
}

